import { RootState } from '@/types/common';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { userService } from '@/services';
import { BaseGroupDetails, EditGroupDetails, SyncGoogleCourses } from '@/types/register';
import { registerService } from '@/services/register';
import { authActions } from '../reducers/auth';
import { TeacherGroup } from '@/types/user';

const teacherDashboardThunks = {
  updateGroupDetails: createAsyncThunk(
    'updateGroupDetails',
    async (
      { groupId, groupDetails }: { groupId: string; groupDetails: BaseGroupDetails },
      { getState, dispatch }
    ) => {
      try {
        const state = getState() as RootState;
        const user = state.auth.user;

        const groupUpdate = await userService.editGroup(user, groupId, groupDetails);

        const updateSucceed = groupUpdate?.data?.data ?? false;

        if (!updateSucceed) {
          throw new Error('Failed to update group details');
        }

        // Add new group to user metadata
        const groupDetailsUpdate: EditGroupDetails = {
          groupId: groupId,
          groupName: groupDetails.groupName.trim(),
          grade: groupDetails.grade.trim(),
        };

        dispatch(authActions.updateTeacherClass(groupDetailsUpdate));

        return {
          groupData: groupDetails,
          groupId: groupId,
        };
      } catch (error) {
        console.log(error);

        throw error;
      }
    }
  ),
  createGroup: createAsyncThunk(
    'createGroup',
    async (groupDetails: BaseGroupDetails, { getState, dispatch }) => {
      try {
        const state = getState() as RootState;
        const user = state.auth.user;

        const teacherGroup = await registerService.createGroup(user, groupDetails);

        const createSucceed = teacherGroup?.data?.data ?? false;

        if (!createSucceed) {
          throw new Error('Failed to update group details');
        }

        // Add new group to user metadata

        dispatch(
          authActions.addNewClass({
            id: createSucceed.groupId,
            name: groupDetails.groupName,
            grade: groupDetails.grade,
            invitationLinkForStudents: '',
            groupLevel: '',
            ...(createSucceed.classCode && { classCode: createSucceed.classCode }),
          })
        );

        return {
          groupData: groupDetails,
          invitationLink: '',
          groupId: createSucceed.groupId,
          ...(createSucceed.classCode && { classCode: createSucceed.classCode }),
        };
      } catch (error) {
        console.log(error);

        throw error;
      }
    }
  ),

  fetchGoogleCourses: createAsyncThunk('googleCourses', async (action: string, { getState }) => {
    try {
      const state = getState() as RootState;
      const user = state.auth.user;

      const googleClasses = await userService.getGoogleCourses(user, action);

      const classes = googleClasses?.data?.data;

      return classes;
    } catch (error) {
      console.log(error);

      throw error;
    }
  }),

  setGoogleCourses: createAsyncThunk(
    'setGoogleCourses',
    async (googleCourses: SyncGoogleCourses[], { getState }) => {
      try {
        const state = getState() as RootState;
        const user = state.auth.user;

        const googleClasses = await userService.setGoogleCourses(user, googleCourses);

        const classes = googleClasses?.data?.data;

        return classes;
      } catch (error) {
        console.log(error);

        throw error;
      }
    }
  ),

  fetchTeacherGroups: createAsyncThunk('fetchTeacherGroups', async (_, { getState, dispatch }) => {
    try {
      const state = getState() as RootState;
      const user = state.auth.user;

      if (!user?.metadata?.organizationDetails?.teacher) {
        throw new Error('User is not a teacher');
      }

      const organizationId = user.metadata.organizationDetails.organizationId;
      const teacherId = user.metadata.id;

      const response = await userService.fetchTeacherGroups(organizationId, teacherId);
      const teacherGroups = response?.data?.data?.teacherGroups;
      if (!teacherGroups) {
        throw new Error('Failed to fetch teacher groups');
      }
      const totalStudents = teacherGroups.reduce(
        (sum: number, teacherGroup: TeacherGroup) => sum + (teacherGroup.totalStudents || 0),
        0
      );
      dispatch(authActions.updateTeacherGroupsData({ teacherGroups, totalStudents }));

      return teacherGroups;
    } catch (error) {
      console.log(error);
      throw error;
    }
  }),
};

export default teacherDashboardThunks;
